import React, { useEffect, useRef, useState } from "react";
import UseContext from "hooks/useContext";
import SectionWrapper from "pages/createMission/components/SectionWrapper";
import Button from "components/button";
import StyledInput from "components/styledInput/StyledInput";
import Tooltip from "components/tooltips/Tooltips";
import DrawnGlyph from "components/emptyState/DrawnGlyph";
import { CrossIcon, Plus } from "assets/icons";
import ApiDataBase from "server";
import TaskNotice from "pages/missionTemplates/components/TaskNotice";
import {
    ADD_TASK_ERROR,
    TASK_INPUT_MAX_LENGTH,
    TASK_NOTICE,
    TASK_SOURCE,
    addCompanyTask,
    addNoticeLineTask,
    addNoticeTask,
    applyTaskCheckResult,
    buildTaskCheckBody,
    canAddNoticeTask,
    cleanTaskLabel,
    countSelectedTasks,
    isTaskBanner,
    isTaskCheckPending,
    removeCompanyTask,
    toggleProposedTask,
} from "pages/missionTemplates/utils/templateTasks";

// Shortest time a notice stays on screen before typing can clear it.
const NOTICE_MIN_VISIBLE_MS = 1500;

const TasksTemplate = () => {
    const { templateData, setTemplateData, language } = UseContext();
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.tasks.${key}`, fallback);
    const [newTask, setNewTask] = useState("");

    const rows = templateData.tasks || [];
    const selectedCount = countSelectedTasks(rows);
    // Proposed or added: any task in the list makes the message pointless.
    const hasTasks = rows.some(
        (row) =>
            row.source === TASK_SOURCE.QUALIFICATION ||
            row.source === TASK_SOURCE.COMPANY
    );
    const canAdd = cleanTaskLabel(newTask) !== "";

    // The notice lives in templateData: the check answer can come back after
    // the user left the step, and must still be shown on return.
    const notice = templateData.taskNotice || null;
    // When the current notice appeared: typing clears it only after a moment.
    const noticeShownAtRef = useRef(0);
    useEffect(() => {
        noticeShownAtRef.current = Date.now();
    }, [notice]);

    const updateRows = (update) =>
        setTemplateData((prev) => ({ ...prev, tasks: update(prev.tasks || []) }));

    const setNotice = (next) =>
        setTemplateData((prev) =>
            (prev.taskNotice || null) === next
                ? prev
                : { ...prev, taskNotice: next }
        );

    // The maximum count error goes away as soon as the user edits the field,
    // as before; the other notices stay until replaced or closed.
    const clearTooManyNotice = () =>
        setTemplateData((prev) =>
            prev.taskNotice?.type === TASK_NOTICE.TOO_MANY
                ? { ...prev, taskNotice: null }
                : prev
        );

    // Functional update: other tasks may have been added, removed or
    // checked meanwhile, and only this row (by key) is touched.
    const settleTaskCheck = (key, response) =>
        setTemplateData((prev) => {
            const result = applyTaskCheckResult(prev.tasks, key, response);
            return {
                ...prev,
                tasks: result.rows,
                ...(result.notice ? { taskNotice: result.notice } : {}),
            };
        });

    const checkAddedTask = (key, label, listedRows) => {
        ApiDataBase.postCheckMissionTemplateTask(
            buildTaskCheckBody(label, templateData.qualification, listedRows),
            (response) => settleTaskCheck(key, response),
            // Network error, timeout, 4xx or 5xx: the typed label stays.
            () => settleTaskCheck(key, null)
        );
    };

    const handleAdd = () => {
        const result = addCompanyTask(rows, newTask);
        if (result.error === ADD_TASK_ERROR.EMPTY) {
            clearTooManyNotice();
            return;
        }
        if (result.error) {
            setNotice({ type: TASK_NOTICE.TOO_MANY });
            return;
        }
        // A grey banner stays until another message replaces it or a new task is typed.
        setTemplateData((prev) => ({
            ...prev,
            tasks: result.rows,
            taskNotice:
                result.notice ||
                (isTaskBanner(prev.taskNotice) ? prev.taskNotice : null),
        }));
        setNewTask("");
        if (result.key) checkAddedTask(result.key, result.label, result.rows);
    };

    const handleAddNoticeTask = () =>
        setTemplateData((prev) => {
            const result = addNoticeTask(prev.tasks, prev.taskNotice);
            return result.error
                ? { ...prev, taskNotice: { type: TASK_NOTICE.TOO_MANY } }
                : { ...prev, tasks: result.rows, taskNotice: null };
        });

    const handleAddNoticeLineTask = (lineId) =>
        setTemplateData((prev) => {
            const result = addNoticeLineTask(prev.tasks, prev.taskNotice, lineId);
            return result.error
                ? { ...prev, taskNotice: { type: TASK_NOTICE.TOO_MANY } }
                : { ...prev, tasks: result.rows, taskNotice: result.notice };
        });

    const handleKeyDown = (e) => {
        if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
        e.preventDefault();
        handleAdd();
    };

    // StyledInput only caps textareas: the input is capped here, as the
    // former maxLength did.
    // The banner has no cross: it goes when the user starts typing a new task
    // (first letter in the empty field), once it has been on screen for a
    // moment. An AI check answer landing while the user types stays until the
    // next new task, so it is not wiped unread.
    const handleChange = (e) => {
        const value = e.target.value.slice(0, TASK_INPUT_MAX_LENGTH);
        const startsNewTask = newTask === "" && value !== "";
        setNewTask(value);
        if (startsNewTask && Date.now() - noticeShownAtRef.current >= NOTICE_MIN_VISIBLE_MS) {
            setNotice(null);
            return;
        }
        clearTooManyNotice();
    };

    const addPlaceholder = t(
        "addPlaceholder",
        "Ajouter une tâche (ex : accueil des livreurs)"
    );
    const removeLabel = t("removeLabel", "Retirer");

    return (
        <SectionWrapper
            title={t("title", "Tâches du poste")}
            description={t(
                "description",
                "Cochez les tâches proposées ou ajoutez les vôtres."
            )}
        >
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                    {/* StyledInput takes no onKeyDown: Enter is caught as it
                        bubbles up from the field. */}
                    <div className="flex-1 min-w-0" onKeyDown={handleKeyDown}>
                        <StyledInput
                            type={"text"}
                            value={newTask}
                            onChange={handleChange}
                            placeholder={addPlaceholder}
                        />
                    </div>
                    <Button
                        size={"SM"}
                        color={canAdd ? "PRIMARY" : "DISABLED"}
                        className={`h-10 shrink-0 ${
                            canAdd ? "" : "cursor-not-allowed"
                        }`}
                        onClick={handleAdd}
                        disabled={!canAdd}
                    >
                        <Plus wh={12} color={canAdd ? "#FFFFFF" : "#9CA3AF"} />
                        {t("addButton", "Ajouter")}
                    </Button>
                </div>
                <TaskNotice
                    notice={notice}
                    showAddLink={canAddNoticeTask(rows, notice)}
                    onAdd={handleAddNoticeTask}
                    canAddLine={(line) => canAddNoticeTask(rows, line)}
                    onAddLine={handleAddNoticeLineTask}
                />
            </div>
            {hasTasks ? (
                // The checkboxes show what is selected; only say why Next is blocked.
                selectedCount === 0 ? (
                    <p className="text-sm text-gray-500" aria-live="polite">
                        {t("selectAtLeastOne", "Cochez au moins une tâche pour continuer.")}
                    </p>
                ) : null
            ) : (
                // Empty state of the step, in the look of the page empty states (badge, title, text).
                <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-100 px-6 py-8 text-center">
                    <div className="relative mb-2 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                        <DrawnGlyph name="squareCheck" size={36} />
                    </div>
                    <p className="text-base font-medium text-gray-900">
                        {t("emptyTitle", "Aucune tâche pour l'instant")}
                    </p>
                    <p className="max-w-md text-sm text-gray-500">
                        {t(
                            "noProposal",
                            "Aucune tâche n'est proposée pour cette qualification. Ajoutez avec le champ ci-dessus celles que l'intérimaire devra réaliser."
                        )}
                    </p>
                </div>
            )}
            {rows.length > 0 && (
                // One bordered list, as the hardship factors above: the checkbox says what is
                // selected, no coloured border.
                <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100 bg-white">
                    {rows.map((row) => {
                        if (row.source !== TASK_SOURCE.COMPANY) {
                            return (
                                <li
                                    key={row.key}
                                    className="flex px-5 py-4 hover:bg-gray-50"
                                >
                                    <StyledInput
                                        type={"checkbox"}
                                        checked={!!row.checked}
                                        onChange={() =>
                                            updateRows((prev) =>
                                                toggleProposedTask(prev, row.key)
                                            )
                                        }
                                        label={
                                            <span className="min-w-0 break-words">
                                                {row.label}
                                            </span>
                                        }
                                    />
                                </li>
                            );
                        }
                        const isPending = isTaskCheckPending(row);
                        return (
                            <li
                                key={row.key}
                                aria-busy={isPending}
                                className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50"
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    {/* Always selected: checked, as the proposed tasks; the cross removes it. */}
                                    <input
                                        type="checkbox"
                                        checked
                                        readOnly
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        className="checkbox-primary pointer-events-none mr-1"
                                    />
                                    {/* While checked: lighter text, no border (the badge carries it). */}
                                    <span className={`text-sm break-words ${isPending ? "text-gray-400" : "text-gray-900"}`}>
                                        {row.label}
                                    </span>
                                </div>
                                {/* The badge sits by the cross: both are about this added task. */}
                                <div className="flex shrink-0 items-center gap-2">
                                    {isPending ? (
                                        <span className="badge badge-sm border border-dashed border-gray-300 bg-white text-gray-400">
                                            {t("checking", "Vérification en cours")}
                                        </span>
                                    ) : (
                                        <span className="badge badge-sm bg-gray-100 text-gray-600">
                                            {t("addedByYou", "Ajoutée par vous")}
                                        </span>
                                    )}
                                    <Tooltip text={removeLabel} placement="top">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                updateRows((prev) =>
                                                    removeCompanyTask(prev, row.key)
                                                )
                                            }
                                            aria-label={`${removeLabel} ${row.label}`}
                                            className="shrink-0 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                        >
                                            <CrossIcon wh={20} color="#111827" />
                                        </button>
                                    </Tooltip>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </SectionWrapper>
    );
};

export default TasksTemplate;
